/**
 * Calls to the admin gateway.
 *
 * Errors branch on the `code` field, never on message text — the API returns
 * `{ "code": "...", "message": "..." }` on every route, and the codes are the
 * contract (docs/products/mcp-api-reference.md). Parsing prose would break the
 * first time someone rewords a message.
 */

export interface ApiErrorBody {
  code?: string;
  message?: string;
  /** Labeeb's error wrapper. Folded up to top-level code/message in `request()`. */
  detail?: unknown;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message || `Request failed (${status})`);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code ?? "";
    this.body = body;
  }
}

/** 401 — we do not know who you are. Re-authenticate. */
export class Unauthenticated extends ApiError {}

/** 403 — we know who you are and you may not. NOT a sign-in problem. */
export class Forbidden extends ApiError {}

/**
 * 404 — but from WHICH layer? Read `fromGateway` before choosing the copy.
 *
 * Two very different things arrive here:
 *
 *   - the PRODUCT saying "not in the verification ledger recently" (365-day
 *     TTL) — the merchant may be perfectly alive, and blocking still works by
 *     reference. Carries `code: "not_found"`.
 *   - the GATEWAY saying "no route matches that path" — a typo or an unmounted
 *     product. Carries no `code` at all, just `{"message": "Not Found"}`.
 *
 * A third meaning exists on DELETE /admin/quota/{tier}/{tool}: "no such
 * override row". Same status, same `code`, different sentence — which is why
 * 404 copy is chosen by the CALLING ROUTE, never by this class alone.
 */
export class NotFound extends ApiError {
  /** True when the gateway rejected the path before any product saw it. */
  get fromGateway(): boolean {
    return !this.code;
  }
}

/**
 * 503 `unavailable` — WE COULD NOT LOOK.
 *
 * The opposite of NotFound, and the reference is explicit that conflating them
 * misleads an operator into reading a transient DynamoDB blip as a deleted
 * merchant. Kept as its own class so no shared handler can collapse the two.
 */
export class Unavailable extends ApiError {}

/** 422 — understood and deliberately refused (bad tier, unknown field, …). */
export class Unprocessable extends ApiError {}

/**
 * 409 `guard_refused` — refused by a guard that `?force=true` can override.
 *
 * The body names what the refusal would break. Surface it verbatim; a generic
 * "failed" throws away the entire point of the guard.
 */
export class GuardRefused extends ApiError {}

/**
 * 409 `version_conflict` — somebody else changed this first.
 *
 * Labeeb's admin API is optimistically concurrent: every change sends the
 * `expected_version` the operator read, and this is the refusal when the
 * stored document has moved on. `current_version` is what it holds NOW, so a
 * caller can re-read and let the operator decide again rather than guess.
 *
 * `force` does not help and must not be offered — there is no guard being
 * bypassed here, only a stale read.
 *
 * Extends GuardRefused so every existing `instanceof GuardRefused` call site
 * keeps working; the narrower classes are additive.
 */
export class VersionConflict extends GuardRefused {
  /** The version the server holds now, when it named one. */
  get currentVersion(): number | undefined {
    const v = (this.body as { current_version?: unknown } | undefined)?.current_version;
    return typeof v === "number" ? v : undefined;
  }
}

/**
 * 409 `already_in_state` — the resource already holds these values.
 *
 * NOT an error to show as a failure. Suspending an already-suspended business
 * is a no-op, and telling the operator "refused" sends them looking for a
 * problem that does not exist. The honest message is "it already is".
 */
export class AlreadyInState extends GuardRefused {}

/**
 * 409 `idempotency_conflict` — this Idempotency-Key was used for a different body.
 *
 * A client bug, not an operator one: the same key must never be reused for a
 * different change. Surfacing it plainly is what makes it findable.
 */
export class IdempotencyConflict extends GuardRefused {}

/**
 * 429 — THE GATEWAY is throttling us, not a product.
 *
 * Neither Labeeb's nor MCP's Lambda returns 429; it comes from API Gateway's
 * own throttling, which means:
 *
 *   - the body is the GATEWAY'S, not the `{detail: {code, message}}`
 *     envelope — so `code` is absent and `message` may be API Gateway's
 *     terse text. Do not key behaviour off `code` here.
 *   - there is NO `Retry-After` header. Any wait this console suggests is its
 *     own guess, so it says "wait a moment" rather than naming a number it
 *     cannot know.
 *
 * Its own class because the remedy — back off and retry — is true of nothing
 * else here. A retry of a CHANGE must reuse the same `Idempotency-Key`, which
 * is what stops a throttled-then-retried write applying twice.
 */
export class RateLimited extends ApiError {}

export interface ApiClientOptions {
  apiUrl: string;
  /**
   * The ID token, NOT the access token — and this is not interchangeable.
   *
   * API Gateway's JWT authorizer validates the `aud` claim against its
   * configured audience. A Cognito ACCESS token has no `aud` at all; it carries
   * `client_id` instead. So an access token is rejected with 401 no matter how
   * fresh it is, and the failure looks exactly like an expired session.
   *
   * The ID token carries `aud`, `email` AND `cognito:groups` — everything the
   * gateway validates and every product Lambda reads.
   *
   * A GETTER rather than a string, so this module never holds a token of its
   * own. Note what that does NOT buy: every caller passes
   * `getIdToken: () => token` closed over a render-scoped value, so what
   * arrives is captured per RENDER, not per call. That is sound today —
   * `automaticSilentRenew` is off and no refresh token is requested, so a
   * token cannot rotate underneath a mounted page — but the shape does not
   * make it impossible, and the comment previously claimed it did.
   */
  getIdToken: () => string | undefined;
}

export interface RequestOptions extends RequestInit {
  /**
   * Query parameters. `force` in particular is a QUERY param on the quota
   * routes, never a body field — deliberately, so it can never be mistaken
   * for a value being applied to a tool.
   */
  query?: Record<string, string | number | boolean | undefined>;
}

export function createApiClient({ apiUrl, getIdToken }: ApiClientOptions) {
  async function request<T>(path: string, init: RequestOptions = {}): Promise<T> {
    const { query, ...rest } = init;
    const token = getIdToken();
    const headers = new Headers(rest.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (rest.body) headers.set("Content-Type", "application/json");

    let url = `${apiUrl}${path}`;
    if (query) {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined) qs.set(k, String(v));
      }
      const s = qs.toString();
      // Path may already carry a query (audit `?days=`). A second `?` would
      // drop the cursor on the floor and the next page would be the first.
      if (s) url += (url.includes("?") ? "&" : "?") + s;
    }

    const resp = await fetch(url, { ...rest, headers });

    if (resp.ok) {
      if (resp.status === 204) return undefined as T;
      return (await resp.json()) as T;
    }

    let body: ApiErrorBody = {};
    try {
      body = (await resp.json()) as ApiErrorBody;
    } catch {
      body = { message: resp.statusText };
    }

    // Labeeb wraps every error as {detail: {code, message, …}}; MCP sends them
    // flat (docs/products/labeeb-api-reference.md). Fold the nested shape up so
    // every ApiError subclass — and NotFound.fromGateway, which reads
    // `!this.code` — sees one contract regardless of which product answered.
    // A real Labeeb 404 must not read as "the gateway has no such route".
    const detail = body.detail;
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
      const d = detail as ApiErrorBody;
      body = {
        ...d,
        ...body,
        code: body.code ?? d.code,
        message: body.message ?? d.message,
      };
    }

    switch (resp.status) {
      case 401:
        throw new Unauthenticated(401, body);
      case 403:
        throw new Forbidden(403, body);
      case 404:
        throw new NotFound(404, body);
      case 409:
        // THREE meanings on Labeeb, one on MCP, all sharing a status. The code
        // is what separates them, and each needs different words in front of
        // an operator: re-read, "it already is", or a client bug. MCP's
        // `guard_refused` keeps the base class, so `?force=true` stays
        // reachable only where it actually applies.
        switch (body.code) {
          case "version_conflict":
            throw new VersionConflict(409, body);
          case "already_in_state":
            throw new AlreadyInState(409, body);
          case "idempotency_conflict":
            throw new IdempotencyConflict(409, body);
          default:
            throw new GuardRefused(409, body);
        }
      case 422:
        throw new Unprocessable(422, body);
      case 429:
        throw new RateLimited(429, body);
      case 503:
        throw new Unavailable(503, body);
      default:
        throw new ApiError(resp.status, body);
    }
  }

  return {
    request,
    health: () => request<{ ok: boolean }>("/mcp/admin/health"),
  };
}
